import React, { useState } from 'react';
import { deleteLabel, reorderLabels, updateLabel } from '../../utils/labelApi';
import { LabelIcon } from './LabelIcons';
import EditLabelModal from './EditLabelModal';
import './labels.css';

const LabelManager = ({ labels = [], onChange }) => {
  const [dragIndex, setDragIndex] = useState(-1);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState('');

  const emitChange = (next) => onChange?.(next);

  const handleDrop = async (targetIndex) => {
    if (dragIndex < 0 || dragIndex === targetIndex) {
      setDragIndex(-1);
      return;
    }
    const next = [...labels];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setDragIndex(-1);
    emitChange(next.map((label, index) => ({ ...label, order: index })));
    try {
      const response = await reorderLabels(next.map((label) => label._id));
      if (response.success) emitChange(response.data);
    } catch (error) {
      console.error('Error reordering labels:', error);
    }
  };

  const handleToggleVisible = async (label) => {
    setBusyId(label._id);
    try {
      const response = await updateLabel(label._id, { visible: !label.visible });
      if (response.success) {
        emitChange(labels.map((entry) => (entry._id === label._id ? response.data : entry)));
      }
    } catch (error) {
      console.error('Error updating label visibility:', error);
    } finally {
      setBusyId('');
    }
  };

  const handleDelete = async (label) => {
    if (!window.confirm(`Delete label "${label.name}"?`)) return;
    setBusyId(label._id);
    try {
      await deleteLabel(label._id);
      emitChange(labels.filter((entry) => entry._id !== label._id));
      window.dispatchEvent(new CustomEvent('labelsUpdated'));
    } catch (error) {
      console.error('Error deleting label:', error);
    } finally {
      setBusyId('');
    }
  };

  return (
    <>
      <div className="label-manager">
        {!labels.length && <div className="labels-empty">No custom labels yet.</div>}
        {labels.map((label, index) => (
          <div
            key={label._id}
            className={`label-manager-row${dragIndex === index ? ' is-dragging' : ''}`}
            draggable
            onDragStart={() => setDragIndex(index)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => handleDrop(index)}
          >
            <span className="label-manager-handle" aria-hidden="true">⋮⋮</span>
            <div className="label-manager-meta">
              <div className="label-manager-name" style={{ color: label.color }}>
                <LabelIcon name={label.icon || 'label'} size={16} color={label.color} />
                {label.name}
              </div>
              <small>{label.visible === false ? 'Hidden in sidebar' : 'Visible in sidebar'} · {label.count || 0} mail</small>
            </div>
            <div className="label-manager-actions">
              <button type="button" onClick={() => setEditing(label)} disabled={busyId === label._id}>Edit</button>
              <button type="button" onClick={() => handleToggleVisible(label)} disabled={busyId === label._id}>
                {label.visible === false ? 'Show' : 'Hide'}
              </button>
              <button type="button" onClick={() => handleDelete(label)} disabled={busyId === label._id}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      <EditLabelModal
        isOpen={Boolean(editing)}
        label={editing}
        onClose={() => setEditing(null)}
        onUpdated={(updated) => {
          emitChange(labels.map((entry) => (entry._id === updated._id ? updated : entry)));
          window.dispatchEvent(new CustomEvent('labelsUpdated'));
        }}
      />
    </>
  );
};

export default LabelManager;
